import { Skins } from "@common/definitions/items/skins";
import { GameConstants } from "@common/constants";
import { EMBLEM, type Emblem, type EmblemOp, EmblemOpType, isBannedColor, sanitizeEmblem } from "@common/utils/emblem";
import { isCollectibleSkin } from "@common/utils/skinCollection";
import { DESIGN_LIMITS, type SkinDesign, sanitizeDesign } from "@common/utils/designs";
import { drawEmblem } from "./utils/emblemRenderer";
import { onSignedInChange } from "./utils/signedIn";
import { t, tr } from "./utils/translations/phrases";

declare const API_URL: string;

/**
 * A design saved while signed out, kept in this browser only (marked `guest`). This used to hold a copy of whatever
 * the signed-in address wore too, so after signing out (or for the next person on the same computer) the game wore
 * that address's design; a copy like that, without the mark, is no longer used.
 */
const STORAGE_KEY = "trenches_skin_local_v3";
/** classic Paint palette, minus anything that reads as ground (grass/sand/mud/asphalt/water) */
const PALETTE = [
    0x000000, 0x800000, 0x008000, 0x008080, 0x000080, 0x800080, 0xff0000, 0xff8000, 0xffff00, 0x00ff00, 0x00ffff, 0x0000ff, 0xff00ff, 0x8000ff,
    0xffffff, 0xc0c0c0, 0xffff80, 0x00ff80, 0x80ffff, 0x8080ff, 0xff0080, 0xff8040, 0xffc0cb, 0xffd166, 0xe63946, 0x4cc9f0, 0x3a86ff, 0xa8b5a0
].filter(c => !isBannedColor(c));
const UNIFORM = GameConstants.player.defaultSkin;
const hex = (c: number): string => `#${c.toString(16).padStart(6, "0")}`;

interface SavedSkin { readonly skin: string, readonly ops: Emblem | undefined, readonly name?: string, readonly drawnOver?: boolean }
type Design = SkinDesign;
/** the design saved in this browser while signed out (read once) */
let saved: SavedSkin | undefined;

/**
 * Designs live on the server, per wallet address (GET/PUT/DELETE /api/profile/designs).
 * `profile` is the cached copy for the address signed in (`profileAddress`); null = no session.
 */
let profile: { designs: Design[], worn: string | null, onMarket: ReadonlySet<string> } | null = null;
let profileAddress: string | undefined;
let profileFetchedAt = 0;
/**
 * bumped when the signed-in address changes: the answer to a request sent before that is the old address's, and is
 * dropped (it could land after the new one's, and the page then wore the old address's design again)
 */
let sessionEpoch = 0;
/** the editor and the menu preview follow the designs on hand (`switched`: they are another address's now) */
let onProfileChanged: ((switched: boolean) => void) | undefined;
/** skins found during this visit, so Load shows them even before the profile API catches up (this address's only) */
const sessionCollected = new Set<string>();

interface DesignsResponse {
    readonly designs: unknown[]
    readonly worn: string | null
    /** the designs on the flea market now, each out of the loadout until it's taken off (0.99) */
    readonly onMarket?: unknown[]
    readonly signedIn?: boolean
    /** whose designs these are (the server says) */
    readonly address?: string
}
const readDesigns = (body: DesignsResponse): { designs: Design[], worn: string | null, onMarket: ReadonlySet<string> } => ({
    designs: body.designs.flatMap(d => { const x = sanitizeDesign(d, UNIFORM); return x ? [x] : []; }),
    worn: body.worn ?? null,
    onMarket: new Set((Array.isArray(body.onMarket) ? body.onMarket : []).flatMap(name => typeof name === "string" ? [name.toLowerCase()] : []))
});
/** whether a design is on the flea market (out of the loadout until it's taken off: 0.99) */
const onMarket = (name: string | undefined): boolean => name !== undefined && profile?.onMarket.has(name.toLowerCase()) === true;
const onMarketWords = (): string => t("It's on the flea market: take it off the market (Store, Flea market, My listings) to use it again.");
/** whose designs an answer holds (an older server doesn't say: the same address's) */
const ownerOf = (body: DesignsResponse): string | undefined => typeof body.address === "string" ? body.address : profileAddress;

/**
 * the designs now on hand, and whose they are: another address than before keeps nothing of the old one (the skins
 * it collected this visit went on showing in Load, after signing in with another address too)
 */
function setProfile(address: string | undefined, next: typeof profile): void {
    const owner = address?.toLowerCase();
    const switched = owner !== profileAddress;
    profileAddress = owner;
    profile = next;
    if (switched) sessionCollected.clear();
    onProfileChanged?.(switched);
}

async function fetchProfile(force = false): Promise<void> {
    if (!force && Date.now() - profileFetchedAt < 10_000) return;
    profileFetchedAt = Date.now();
    const epoch = sessionEpoch;
    try {
        const res = await fetch(`${API_URL}/profile/designs`, { credentials: "include", cache: "no-store" });
        if (epoch !== sessionEpoch) return;
        if (res.status === 401) { setProfile(undefined, null); return; }
        if (!res.ok) return;
        const body = await res.json() as DesignsResponse;
        if (epoch !== sessionEpoch) return;
        if (body.signedIn === false) { setProfile(undefined, null); return; }
        setProfile(ownerOf(body), readDesigns(body));
    } catch { /* offline or no session: keep whatever we had */ }
}
/** the server's words for a refusal (in the player's language when the phrasebook has them), else what failed */
const errorOf = async(res: Response, what: "Save" | "Delete"): Promise<string> => {
    const failed = (): string => (what === "Save" ? t("Save failed (<status>)", { status: res.status }) : t("Delete failed (<status>)", { status: res.status }));
    try {
        const error = ((await res.json()) as { error?: string }).error;
        return (error ? tr(error) : error) ?? failed();
    } catch { return failed(); }
};
async function putDesign(design: Design, wear: boolean): Promise<string | undefined> {
    const epoch = sessionEpoch;
    try {
        const res = await fetch(`${API_URL}/profile/designs`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ design, wear }) });
        if (res.status === 401) return t("Play as a guest or sign in with your wallet first: designs are saved to your sign-in.");
        if (!res.ok) return await errorOf(res, "Save");
        const body = await res.json() as DesignsResponse;
        if (epoch !== sessionEpoch) return t("Your address changed while saving: open Load to see your designs.");
        setProfile(ownerOf(body), readDesigns(body));
        profileFetchedAt = Date.now();
        return undefined;
    } catch { return t("Save failed: no connection to the server"); }
}
async function removeDesign(name: string): Promise<string | undefined> {
    const epoch = sessionEpoch;
    try {
        const res = await fetch(`${API_URL}/profile/designs?name=${encodeURIComponent(name)}`, { method: "DELETE", credentials: "include" });
        if (!res.ok) return await errorOf(res, "Delete");
        const body = await res.json() as DesignsResponse;
        if (epoch !== sessionEpoch) return undefined;
        setProfile(ownerOf(body), readDesigns(body));
        profileFetchedAt = Date.now();
        return undefined;
    } catch { return t("Delete failed: no connection to the server"); }
}
/** what this address wears: the worn design from the server, else the browser-local one (no session) */
function worn(): SavedSkin {
    const d = profile?.worn ? profile.designs.find(x => x.name === profile?.worn) : undefined;
    // (one on the flea market isn't worn: the uniform, as the server has it, 0.99)
    if (d && !onMarket(d.name)) return { skin: d.skin, ops: d.ops, name: d.name, drawnOver: d.drawnOver === true || d.skin !== UNIFORM };
    if (profile) return { skin: UNIFORM, ops: undefined };
    return load();
}

function load(): SavedSkin {
    if (saved) return saved;
    saved = { skin: UNIFORM, ops: undefined };
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as { ops?: unknown, name?: unknown, guest?: unknown } | null;
        if (raw?.guest === true) saved = { skin: UNIFORM, ops: sanitizeEmblem(raw.ops), name: typeof raw.name === "string" ? raw.name : undefined };
        // a copy of a signed-in address's design (from before): not this browser's own, and not worn by anyone else
        else if (raw !== null) localStorage.removeItem(STORAGE_KEY);
    } catch { /* storage unavailable or unreadable: nothing saved here */ }
    return saved;
}
function loadLibrary(): Design[] { return profile?.designs ?? []; }
/** a design saved while signed out: this browser only, on the uniform (collected skins are an address's) */
function storeGuest(ops: Emblem | undefined, name: string): void {
    saved = { skin: UNIFORM, ops, name };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...saved, guest: true })); } catch { /* storage unavailable */ }
}

/**
 * The menu's sign-in changed (signed in, out, or another address, here or in another tab): nothing kept for the old
 * address stays (its designs, the skins it collected this visit), and the new one's designs are asked for at once.
 * Until they arrive the page wears the uniform (signed in) or this browser's own design (signed out).
 */
function noteSignedInAddress(address: string | undefined): void {
    const next = address?.toLowerCase();
    if (next === profileAddress) return;
    sessionEpoch++;
    profileFetchedAt = 0;
    setProfile(next, next === undefined ? null : { designs: [], worn: null, onMarket: new Set() });
    if (next !== undefined) void fetchProfile(true);
}
onSignedInChange(noteSignedInAddress);

/** The emblem this address wears (validated), or undefined. */
export function getSavedEmblem(): Emblem | undefined { return worn().ops; }
/**
 * the designs saved to the address signed in (the flea market's Sell lists them, 0.98): their names and drawings, whether
 * each was drawn over a skin (never sold) and whether it's on the flea market (out of the loadout; 0.99)
 */
export function savedDesigns(): ReadonlyArray<{ readonly name: string, readonly ops: Emblem | undefined, readonly drawnOver: boolean, readonly onMarket: boolean }> {
    return loadLibrary().map(design => ({ name: design.name, ops: design.ops, drawnOver: design.drawnOver === true || design.skin !== UNIFORM, onMarket: onMarket(design.name) }));
}
/** the address's designs read again (the flea market's Sell, as it opens) */
export function refreshSavedDesigns(): Promise<void> { return fetchProfile(true); }
/** The base skin this address wears (a collected one, or the uniform). */
export function getSavedSkin(): string { return worn().skin; }
/** re-read the address's designs from the server (called at startup, on menu return, before joining) */
export function refreshWornSkin(force = false): Promise<void> { return fetchProfile(force); }
export function noteSkinCollected(id: string): void { sessionCollected.add(id); }

// ---------- base skin drawing (the picture under the emblem) ----------
const skinImages = new Map<string, HTMLImageElement>();
function skinImage(key: string, onload: () => void): HTMLImageElement | undefined {
    let img = skinImages.get(key);
    if (img) return img.complete && img.naturalWidth > 0 ? img : undefined;
    img = new Image();
    img.onload = onload;
    img.src = `./img/game/skins/${key}.svg`;
    skinImages.set(key, img);
    return undefined;
}
/** draws the base skin (uniform or a collected skin) as a body-sized disc; calls `onload` when an image finishes loading */
export function drawBaseSkin(ctx: CanvasRenderingContext2D, skinId: string, size: number, onload: () => void): void {
    const def = Skins.fromStringSafe(skinId) ?? Skins.fromString(UNIFORM);
    const r = size * 0.47;
    if (def.baseTint !== undefined) {
        ctx.beginPath(); ctx.arc(size / 2, size / 2, r, 0, Math.PI * 2);
        ctx.fillStyle = hex(def.baseTint); ctx.fill();
        ctx.lineWidth = Math.max(1, size * 0.02); ctx.strokeStyle = "rgba(0,0,0,0.55)"; ctx.stroke();
        return;
    }
    const img = skinImage(def.baseImage ?? `${def.idString}_base`, onload);
    if (img) ctx.drawImage(img, size / 2 - r, size / 2 - r, r * 2, r * 2);
    else { ctx.beginPath(); ctx.arc(size / 2, size / 2, r, 0, Math.PI * 2); ctx.fillStyle = "#888"; ctx.fill(); }
}

/** an image drawn with a multiplicative tint, like the game's sprite tint */
const tintCache = new Map<string, HTMLCanvasElement>();
function tinted(img: HTMLImageElement, tint: number, key: string): HTMLCanvasElement {
    let c = tintCache.get(key);
    if (c) return c;
    c = document.createElement("canvas");
    c.width = img.naturalWidth * 4; c.height = img.naturalHeight * 4;
    const x = c.getContext("2d");
    if (x) {
        x.drawImage(img, 0, 0, c.width, c.height);
        if (tint !== 0xffffff) {
            x.globalCompositeOperation = "multiply"; x.fillStyle = hex(tint); x.fillRect(0, 0, c.width, c.height);
            x.globalCompositeOperation = "destination-in"; x.drawImage(img, 0, 0, c.width, c.height);
        }
    }
    tintCache.set(key, c);
    return c;
}

/**
 * The player as the menu shows it: body (base skin + emblem) facing up with both fists, like the
 * in-game idle pose. Fists follow the skin's rules (tinted plain fist, or the skin's own fist art).
 */
/**
 * The store's picture emblem the player wears instead of the drawn one (0.92), shown on the menu's player too: its name
 * and its picture, set by the store's menu (storeMenu.ts) once it knows what the wallet wears
 */
let storePicture: { readonly name: string, readonly image: HTMLImageElement } | undefined;
let onStorePictureChanged: (() => void) | undefined;
/** the store picture worn instead of a drawn emblem, if one is (the end screens' soldier, 1.05) */
export function wornPicture(): HTMLImageElement | undefined { return storePicture?.image; }

export function showStoreEmblem(picture: { readonly name: string, readonly image: HTMLImageElement } | undefined): void {
    if (storePicture?.image === picture?.image && storePicture?.name === picture?.name) return;
    storePicture = picture;
    onStorePictureChanged?.();
}

/** a picture in the emblem's circle, as the match draws it (storeCatalog.ts: kept in proportion, centred, filling it) */
const drawPicture = (ctx: CanvasRenderingContext2D, image: HTMLImageElement, size: number): void => {
    const width = image.naturalWidth || size;
    const height = image.naturalHeight || size;
    const scale = Math.max(size / width, size / height);
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size * 0.47, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(image, (size - width * scale) / 2, (size - height * scale) / 2, width * scale, height * scale);
    ctx.restore();
};

export function paintPlayerPreview(canvas: HTMLCanvasElement, skinId: string, emblem: Emblem | undefined, picture?: HTMLImageElement): void {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const S = canvas.width;
    const def = Skins.fromStringSafe(skinId) ?? Skins.fromString(UNIFORM);
    const bodyR = S * 0.3;                         // body radius on this canvas
    const fistR = bodyR * (17 / 45);               // fist 34 px vs body 90 px in game
    const cx = S / 2, cy = S * 0.58;
    const body = document.createElement("canvas");
    body.width = body.height = Math.round((bodyR * 2) / 0.94);
    const paint = (): void => {
        const b = body.getContext("2d");
        if (!b) return;
        b.clearRect(0, 0, body.width, body.height);
        drawBaseSkin(b, def.idString, body.width, paint);
        if (picture) drawPicture(b, picture, body.width);
        else drawEmblem(b, emblem, body.width);
        ctx.clearRect(0, 0, S, S);
        ctx.beginPath(); ctx.ellipse(cx, cy + bodyR * 0.95, bodyR * 0.95, bodyR * 0.28, 0, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.22)"; ctx.fill();
        ctx.drawImage(body, cx - body.width / 2, cy - body.height / 2);
        const fistFrame = def.fistTint !== undefined ? "plain_fist" : `${def.idString}_fist`;
        const fistImg = skinImage(fistFrame, paint);
        for (const side of [-1, 1]) {
            const fx = cx + side * bodyR * 0.78, fy = cy - bodyR * 0.84;
            if (fistImg) {
                const t = tinted(fistImg, def.fistTint ?? 0xffffff, `${fistFrame}:${def.fistTint ?? "none"}`);
                ctx.drawImage(t, fx - fistR, fy - fistR, fistR * 2, fistR * 2);
            } else {
                ctx.beginPath(); ctx.arc(fx, fy, fistR, 0, Math.PI * 2);
                ctx.fillStyle = hex(def.fistTint ?? 0xb4a894); ctx.fill();
                ctx.lineWidth = Math.max(1, fistR * 0.18); ctx.strokeStyle = "#202020"; ctx.stroke();
            }
        }
    };
    paint();
}

/** draws base skin + emblem into a small canvas (menu preview, list rows) */
export function paintSkinPreview(canvas: HTMLCanvasElement, skinId: string, emblem: Emblem | undefined): void {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const s = canvas.width;
    const paint = (): void => { ctx.clearRect(0, 0, s, s); drawBaseSkin(ctx, skinId, s, paint); drawEmblem(ctx, emblem, s); };
    paint();
}

export function initEmblemEditor(): void {
    const win = document.getElementById("emblem-window");
    const loadWin = document.getElementById("emblem-load-window");
    const saveWin = document.getElementById("emblem-save-window");
    const openBtn = document.getElementById("btn-emblem");
    const canvas = document.getElementById("emblem-canvas") as HTMLCanvasElement | null;
    const preview = document.getElementById("customize-preview") as HTMLCanvasElement | null;
    const wearingLabel = document.getElementById("customize-wearing");
    if (!win || !loadWin || !saveWin || !openBtn || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;
    const tools = { pencil: $("emblem-tool-pencil"), eraser: $("emblem-tool-eraser"), circle: $("emblem-tool-circle") };
    const colorInput = $<HTMLInputElement>("emblem-color");
    const current = $("emblem-current");
    const status = $("emblem-status");
    const counter = $("emblem-counter");
    const swatches = $("emblem-swatches");
    const baseLabel = $("emblem-base-label");
    const widths = Array.from(document.querySelectorAll<HTMLElement>("#emblem-options .ew-width"));
    const fills = Array.from(document.querySelectorAll<HTMLElement>("#emblem-fillstyle button"));

    let ops: EmblemOp[] = [...(worn().ops ?? [])];
    let baseSkin = worn().skin;
    let designName: string | undefined = worn().name;
    /**
     * the drawing on the canvas drawn over a skin, now or before (Load keeps it as the base changes): saved so, it's never
     * sold on the flea market (0.99, the owner: "make emblems drawn over unsellable, and only those who are original
     * legibile"). A cleared canvas is an original again
     */
    let drawnOver = worn().drawnOver === true;
    type Tool = "pencil" | "eraser" | "circle";
    let tool: Tool = "pencil";
    let color = 0xffffff;
    let width = 8;
    let filled = false;
    let drawing: EmblemOp | undefined;
    let dragStart: [number, number] | undefined;
    let warnTimer: ReturnType<typeof setTimeout> | undefined;
    /** the canvas shows what the address wears, and follows it (until something is drawn or loaded) */
    let followWorn = true;

    const S = canvas.width;
    const isStroke = (o: EmblemOp): o is Extract<EmblemOp, { p: number[] }> => o.t === EmblemOpType.Pencil || o.t === EmblemOpType.Eraser;
    const pointsUsed = (): number => ops.reduce((n, o) => n + (isStroke(o) ? o.p.length / 2 : 0), 0);
    const toolLabel = (): string => (tool === "pencil"
        ? t("Pencil \u00B7 <width> px", { width })
        : tool === "circle"
            ? filled ? t("Circle (filled) \u00B7 <width> px", { width }) : t("Circle (outline) \u00B7 <width> px", { width })
            : t("Eraser \u00B7 <width> px", { width }));
    const say = (text: string, warn = false): void => {
        status.textContent = text; status.classList.toggle("warn", warn);
        if (warnTimer) clearTimeout(warnTimer);
        if (warn) warnTimer = setTimeout(() => { status.classList.remove("warn"); status.textContent = toolLabel(); }, 2500);
    };
    const skinName = (id: string): string => id === UNIFORM ? t("Uniform") : (Skins.fromStringSafe(id)?.name ?? id);

    const redraw = (): void => {
        ctx.clearRect(0, 0, S, S);
        drawBaseSkin(ctx, baseSkin, S, redraw);
        drawEmblem(ctx, drawing ? [...ops, drawing] : ops, S);
        counter.textContent = t("<shapes>/<max_shapes> shapes, <points>/<max_points> pencil points", { shapes: ops.length, max_shapes: EMBLEM.maxOps, points: pointsUsed(), max_points: EMBLEM.maxPointsTotal });
        baseLabel.textContent = t("Base: <skin>", { skin: skinName(baseSkin) });
    };
    const refreshPreview = (): void => {
        const w = worn();
        if (preview) paintPlayerPreview(preview, w.skin, w.ops, storePicture?.image);
        if (wearingLabel) {
            wearingLabel.textContent = storePicture
                ? t("Wearing \u201c<name>\u201d from the Store", { name: storePicture.name })
                : w.name ? t("Wearing \u201c<name>\u201d", { name: w.name }) : w.skin === UNIFORM ? t("Wearing the uniform") : t("Wearing <skin>", { skin: skinName(w.skin) });
        }
    };
    onStorePictureChanged = refreshPreview;
    const showWorn = (): void => {
        const w = worn();
        ops = [...(w.ops ?? [])]; baseSkin = w.skin; designName = w.name; drawnOver = w.drawnOver === true; drawing = undefined; dragStart = undefined;
        redraw();
    };
    // another address signed in (or out): its designs and collection are shown, and nothing of the old one is left
    // open (the Load window listed the old address's designs, and saving one copied it to the new address)
    onProfileChanged = (switched: boolean): void => {
        if (switched) {
            followWorn = true;
            loadWin.style.display = "none";
            saveWin.style.display = "none";
        }
        refreshPreview();
        if (followWorn) showWorn();
    };

    const setTool = (t: Tool): void => {
        tool = t;
        for (const [k, b] of Object.entries(tools)) b.classList.toggle("active", k === t);
        $("emblem-fillstyle").style.visibility = t === "circle" ? "visible" : "hidden";
        say(toolLabel());
    };
    for (const [k, b] of Object.entries(tools)) b.addEventListener("click", () => setTool(k as Tool));
    for (const w of widths) {
        w.addEventListener("click", () => {
            widths.forEach(x => x.classList.remove("active")); w.classList.add("active");
            width = Number(w.dataset.w); say(toolLabel());
        });
    }
    for (const f of fills) {
        f.addEventListener("click", () => {
            fills.forEach(x => x.classList.remove("active")); f.classList.add("active");
            filled = f.dataset.fill === "1"; say(toolLabel());
        });
    }

    const setColor = (c: number): boolean => {
        if (isBannedColor(c)) { say(t("That colour is too close to the ground and can't be used."), true); return false; }
        color = c; current.style.background = hex(c); colorInput.value = hex(c);
        return true;
    };
    for (const c of PALETTE) {
        const sw = document.createElement("button");
        sw.type = "button"; sw.className = "ew-swatch"; sw.style.background = hex(c); sw.title = hex(c);
        sw.addEventListener("click", () => setColor(c));
        swatches.appendChild(sw);
    }
    colorInput.addEventListener("input", () => { if (!setColor(parseInt(colorInput.value.slice(1), 16))) colorInput.value = hex(color); });
    setColor(0xffffff);

    const toEmblemSpace = (e: PointerEvent): [number, number] => {
        const r = canvas.getBoundingClientRect();
        const x = Math.round((e.clientX - r.left) / r.width * EMBLEM.size); const y = Math.round((e.clientY - r.top) / r.height * EMBLEM.size);
        return [Math.max(0, Math.min(EMBLEM.size - 1, x)), Math.max(0, Math.min(EMBLEM.size - 1, y))];
    };
    canvas.addEventListener("pointerdown", e => {
        if (e.button !== 0) return;
        canvas.setPointerCapture(e.pointerId);
        const [x, y] = toEmblemSpace(e);
        if (ops.length >= EMBLEM.maxOps) { say(t("Shape limit reached, undo something first."), true); return; }
        if (tool === "circle") {
            dragStart = [x, y];
            drawing = { t: EmblemOpType.Circle, c: color, x, y, r: 1, f: filled, w: Math.max(1, Math.round(width / 2)) };
        } else {
            if (pointsUsed() >= EMBLEM.maxPointsTotal) { say(t("Drawing limit reached, undo something first."), true); return; }
            drawing = { t: tool === "eraser" ? EmblemOpType.Eraser : EmblemOpType.Pencil, c: color, w: width, p: [x, y] };
        }
        followWorn = false;
        redraw();
    });
    canvas.addEventListener("pointermove", e => {
        if (!drawing) return;
        const [x, y] = toEmblemSpace(e);
        if (drawing.t === EmblemOpType.Circle && dragStart) {
            drawing.r = Math.max(1, Math.min(EMBLEM.maxRadius, Math.round(Math.hypot(x - dragStart[0], y - dragStart[1]))));
        } else if (isStroke(drawing)) {
            const p = drawing.p; const lx = p[p.length - 2], ly = p[p.length - 1];
            if (Math.hypot(x - lx, y - ly) >= 3 && p.length / 2 < EMBLEM.maxPointsPerStroke && pointsUsed() + p.length / 2 < EMBLEM.maxPointsTotal) p.push(x, y);
        }
        redraw();
    });
    const finish = (): void => {
        if (drawing) {
            ops.push(drawing); drawing = undefined; dragStart = undefined;
            // (drawn while a skin is the base: drawn over it)
            if (baseSkin !== UNIFORM) drawnOver = true;
        }
        redraw();
    };
    canvas.addEventListener("pointerup", finish);
    canvas.addEventListener("pointercancel", finish);

    const undo = (): void => { ops.pop(); if (!ops.length) drawnOver = false; followWorn = false; redraw(); };
    const clear = (): void => { ops = []; drawnOver = false; followWorn = false; redraw(); };

    // ---------- Save: name it, keep it in the library, wear it ----------
    const nameInput = $<HTMLInputElement>("emblem-save-name");
    const openSave = (): void => {
        nameInput.value = designName ?? "";
        saveWin.style.display = "";
        setTimeout(() => nameInput.focus(), 0);
    };
    const commitSave = async(): Promise<void> => {
        const name = nameInput.value.trim().slice(0, DESIGN_LIMITS.maxNameLength);
        if (!name) { nameInput.focus(); return; }
        if (onMarket(name)) { say(onMarketWords(), true); return; }
        const design: Design = { name, skin: baseSkin, ops: sanitizeEmblem(ops), savedAt: Date.now(), ...(drawnOver || baseSkin !== UNIFORM ? { drawnOver: true } : {}) };
        if (profile === null) {
            // no address yet: keep it in this browser only (on the uniform: collected skins belong to an address)
            storeGuest(design.ops, name);
            saveWin.style.display = "none";
            followWorn = true; showWorn(); refreshPreview();
            say(t("Saved in this browser only. Play as a guest or sign in with your wallet to keep designs on your sign-in."), true);
            return;
        }
        const error = await putDesign(design, true);
        if (error) { say(error, true); return; }
        // what the server kept: a base skin this address hasn't collected became the uniform (the editor went on
        // showing it, as if it had been saved)
        const kept = loadLibrary().find(d => d.name.toLowerCase() === name.toLowerCase());
        const baseChanged = kept !== undefined && kept.skin !== baseSkin;
        followWorn = true; showWorn(); refreshPreview();
        saveWin.style.display = "none";
        if (baseChanged) say(t(`Saved "<name>" on the uniform: that base skin isn't in this address's collection.`, { name }), true);
        // (a picture from the Store worn: a match shows it, not the drawing, until "My own drawing" is picked in the
        // Store's Mine tab; 0.94 audit, it said "You're wearing it now")
        else if (storePicture) say(t(`Saved "<name>". You still wear the Store's "<picture>": to wear this one, pick "My own drawing" in the Store's Mine tab.`, { picture: storePicture.name, name }));
        else say(t(`Saved "<name>" to your address. You're wearing it now; it's in Load too.`, { name }));
    };
    const close = (): void => { followWorn = true; showWorn(); win.style.display = "none"; loadWin.style.display = "none"; saveWin.style.display = "none"; };

    // ---------- Load window: the collection ----------
    const list = $("emblem-skin-list"); const hint = $("emblem-load-hint"); const deleteBtn = $<HTMLButtonElement>("emblem-load-delete");
    const duplicateBtn = $<HTMLButtonElement>("emblem-load-duplicate");
    type Pick = { kind: "base", id: string } | { kind: "design", name: string };
    let selected: Pick = { kind: "base", id: UNIFORM };
    const pickKey = (pick: Pick): string => pick.kind === "design" ? `design:${pick.name}` : `base:${pick.id}`;
    const select = (el: HTMLElement, pick: Pick): void => {
        list.querySelectorAll(".ewl-row").forEach(r => r.classList.remove("selected")); el.classList.add("selected"); selected = pick;
        // (one on the flea market is out of the loadout: never loaded, copied or deleted until it's taken off; 0.99)
        const locked = pick.kind === "design" && onMarket(pick.name);
        deleteBtn.disabled = pick.kind !== "design" || locked;
        duplicateBtn.disabled = pick.kind !== "design" || locked;
        if (locked) hint.textContent = onMarketWords();
    };
    const row = (pick: Pick, label: string, skin: string, emblem: Emblem | undefined, tag?: string): HTMLElement => {
        const el = document.createElement("div"); el.className = "ewl-row"; el.dataset.pick = pickKey(pick);
        const c = document.createElement("canvas"); c.width = c.height = 56; paintSkinPreview(c, skin, emblem);
        const text = document.createElement("span"); text.textContent = label;
        el.append(c, text);
        if (tag) { const t = document.createElement("span"); t.className = "ewl-tag"; t.textContent = tag; el.appendChild(t); }
        el.addEventListener("click", () => select(el, pick));
        el.addEventListener("dblclick", () => { select(el, pick); applyLoad(); });
        return el;
    };
    const head = (text: string): HTMLElement => { const h = document.createElement("div"); h.className = "ewl-head"; h.textContent = text; return h; };
    const applyLoad = (): void => {
        followWorn = false;
        if (selected.kind === "design") {
            if (onMarket(selected.name)) { hint.textContent = onMarketWords(); return; }
            const d = loadLibrary().find(x => x.name === (selected as { name: string }).name);
            if (d) { baseSkin = d.skin; ops = [...(d.ops ?? [])]; designName = d.name; drawnOver = d.drawnOver === true || d.skin !== UNIFORM; }
            loadWin.style.display = "none"; redraw(); say(t(`Loaded "<name>". Edit it, then Save.`, { name: String(designName) }));
        } else {
            baseSkin = selected.id; designName = undefined;
            loadWin.style.display = "none"; redraw(); say(t("Loaded <skin> as the base. Draw over it, then Save.", { skin: skinName(baseSkin) }));
        }
    };
    const deleteSelected = async(): Promise<void> => {
        if (selected.kind !== "design") return;
        if (onMarket(selected.name)) { hint.textContent = onMarketWords(); return; }
        const name = selected.name;
        const error = await removeDesign(name);
        if (error) { hint.textContent = error; return; }
        if (designName === name) designName = undefined;
        refreshPreview();
        void openLoad();
    };
    const openLoad = async(): Promise<void> => {
        await fetchProfile(true);
        const wearing = worn();
        list.replaceChildren();
        list.appendChild(head(t("My designs")));
        const library = loadLibrary();
        if (library.length === 0) { const e = document.createElement("div"); e.className = "ewl-row"; e.textContent = profile === null ? t("Play as a guest or sign in with your wallet to see the designs saved to it.") : t("Nothing saved yet. Draw something and press Save."); list.appendChild(e); }
        for (const d of library) list.appendChild(row({ kind: "design", name: d.name }, d.name, d.skin, d.ops, onMarket(d.name) ? t("on the flea market") : wearing.name === d.name ? t("wearing") : undefined));
        list.appendChild(head(t("Base skins")));
        list.appendChild(row({ kind: "base", id: UNIFORM }, t("Uniform (default)"), UNIFORM, undefined));
        selected = designName ? { kind: "design", name: designName } : { kind: "base", id: baseSkin };
        deleteBtn.disabled = selected.kind !== "design" || onMarket(designName);
        duplicateBtn.disabled = selected.kind !== "design" || onMarket(designName);
        hint.textContent = t("Loading your collection…");
        loadWin.style.display = "";
        let ids: { id: string, name: string }[] = [];
        try {
            const res = await fetch(`${API_URL}/profile/skins`, { credentials: "include" });
            if (res.status === 401) { hint.textContent = t("Sign in with your wallet (or play as a guest) to see the skins you collected."); }
            else if (!res.ok) { hint.textContent = t("Collected skins unavailable right now."); }
            else { ids = (await res.json() as { skins: { id: string, name: string }[] }).skins; hint.textContent = t("Your saved designs, and the skins you collected in matches."); }
        } catch { hint.textContent = t("Collected skins unavailable right now."); }
        const known = new Set(ids.map(s => s.id));
        for (const id of sessionCollected) if (!known.has(id) && isCollectibleSkin(id)) ids.push({ id, name: Skins.fromStringSafe(id)?.name ?? id });
        for (const s of ids) list.appendChild(row({ kind: "base", id: s.id }, s.name, s.id, undefined));
        if (ids.length === 0) { const e = document.createElement("div"); e.className = "ewl-row"; e.textContent = t("No collected skins yet. They show up as loot in matches."); list.appendChild(e); }
        // re-select the current one (found by what the row is: by the start of its text, "Cool" lit up the row of
        // "Cool 2" while Delete would have deleted "Cool")
        const wanted = pickKey(selected);
        const target = Array.from(list.querySelectorAll<HTMLElement>(".ewl-row")).find(r => r.dataset.pick === wanted);
        if (target) target.classList.add("selected");
    };

    $("emblem-undo")?.addEventListener("click", undo);
    $("emblem-tool-undo").addEventListener("click", undo);
    $("emblem-clear").addEventListener("click", clear);
    $("emblem-save").addEventListener("click", openSave);
    $("emblem-save-ok").addEventListener("click", () => { void commitSave(); });
    $("emblem-save-cancel").addEventListener("click", () => { saveWin.style.display = "none"; });
    $("emblem-save-close").addEventListener("click", () => { saveWin.style.display = "none"; });
    nameInput.addEventListener("keydown", e => { if (e.key === "Enter") { e.preventDefault(); void commitSave(); } e.stopPropagation(); });
    $("emblem-load").addEventListener("click", () => { void openLoad(); });
    $("emblem-load-ok").addEventListener("click", applyLoad);
    $("emblem-load-delete").addEventListener("click", () => { void deleteSelected(); });
    duplicateBtn.addEventListener("click", () => {
        if (selected.kind !== "design" || onMarket(selected.name)) return;
        const source = loadLibrary().find(d => d.name === (selected as { name: string }).name);
        if (!source) return;
        const taken = new Set(loadLibrary().map(d => d.name.toLowerCase()));
        const base = `${source.name} copy`.slice(0, DESIGN_LIMITS.maxNameLength);
        let name = base;
        for (let n = 2; taken.has(name.toLowerCase()); n++) name = `${base.slice(0, DESIGN_LIMITS.maxNameLength - String(n).length - 1)} ${n}`;
        void putDesign({ ...source, name, savedAt: Date.now() }, false).then(error => {
            if (error) { hint.textContent = error; return; }
            selected = { kind: "design", name };
            void openLoad();
        });
    });
    $("emblem-load-cancel").addEventListener("click", () => { loadWin.style.display = "none"; });
    $("emblem-load-close").addEventListener("click", () => { loadWin.style.display = "none"; });
    $("close-emblem").addEventListener("click", close);
    $("emblem-min").addEventListener("click", close);
    $("emblem-max").addEventListener("click", () => { /* decorative */ });
    document.addEventListener("keydown", e => {
        if (win.style.display === "none") return;
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); openSave(); }
        if (e.key === "Escape") {
            if (saveWin.style.display !== "none") saveWin.style.display = "none";
            else if (loadWin.style.display !== "none") loadWin.style.display = "none";
            else close();
        }
    });
    openBtn.addEventListener("click", () => {
        void fetchProfile(true).then(() => {
            win.style.display = "";
            followWorn = true; showWorn();
            setTool("pencil");
        });
    });

    // keep the worn design in sync with whichever address is signed in
    void fetchProfile(true).then(() => { refreshPreview(); if (followWorn) showWorn(); });
    setInterval(() => { void fetchProfile().then(refreshPreview); }, 15_000);
    setTool("pencil");
    redraw();
}
